import React, {useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {getRuleData} from "../../services/medicalRulesService";
import "./MedicalRules.css";

/**
 * The body of the rule-detail modal - a port of the Angular `app-medical-rules`.
 *
 * The modal chrome (dialog, title bar, close button) belongs to the caller; this renders
 * only what goes inside it.
 *
 * `content` on every card and statement is HTML built by `medicalRulesService`, which
 * escapes every value it interpolates. See the note at the top of that module before
 * touching a `dangerouslySetInnerHTML` here.
 */

const borderColor = (r) => (
    r === true ? '2px solid #4caf50' : r === false ? '2px solid #f44336' : '2px solid #e0e0e0'
);

const statementBg = (r) => (r === true ? '#f1f8f4' : r === false ? '#fff3f3' : '#ffffff');

/** The engine calls it the Clinical Terms Rule; the screen calls it the Evidence Rule. */
const headingFor = (title) => (title === 'Clinical Terms Rule' ? 'Evidence Rule' : title);

function MedicalRules(props) {
    // `viewType` is part of the component's contract - see propTypes - but the filter below
    // keys off `type` alone, and the service is always asked for its default simple view.
    const {jsonData, type} = props;

    const ruleData = useMemo(() => {
        const all = jsonData ? getRuleData(jsonData) : [];
        // The original spelled this out as five ifs with the same effect: the rule types
        // filter down to their own card, and every other type falls through unfiltered.
        return type === 'Protocols' ? all : all.filter((rule) => rule.title === type);
        // FIX: the Angular version derived this in ngOnInit only, so reopening the modal on a
        // different claim rendered the rules of the previous one. A memo recomputes whenever
        // the inputs change.
    }, [jsonData, type]);

    // Expansion is React state keyed by position, never a mutation of the rule objects the
    // service handed over - those are rebuilt on every derivation.
    const seed = useMemo(() => {
        const map = {};
        ruleData.forEach((rule, ruleIndex) => {
            map[`${ruleIndex}`] = rule.expanded !== false;
            (rule.statements || []).forEach((statement) => {
                map[`${ruleIndex}:${statement.id}`] = statement.expanded !== false;
            });
        });
        return map;
    }, [ruleData]);

    const [expanded, setExpanded] = useState(seed);
    useEffect(() => setExpanded(seed), [seed]);

    const toggle = (key) => setExpanded((current) => ({...current, [key]: !current[key]}));

    return (
        <div className="protocols-container">
            <h2 className="protocols-title">Protocols</h2>

            {ruleData.length === 0 && (
                <div className="content-wrapper">No rule detail is available for this procedure.</div>
            )}

            {ruleData.map((rule, ruleIndex) => {
                const ruleKey = `${ruleIndex}`;
                const isRuleOpen = Boolean(expanded[ruleKey]);

                return (
                    <div className="rule-container" key={`${rule.title}-${ruleIndex}`}>
                        <h6>{headingFor(rule.title)}</h6>

                        {!rule.hasNestedRules ? (
                            <div className="rule-box" style={{border: borderColor(rule.conditionResult)}}>
                                <button
                                    type="button"
                                    className="toggle-btn mb-1"
                                    onClick={() => toggle(ruleKey)}
                                >
                                    {isRuleOpen ? '−' : '+'}
                                </button>

                                {isRuleOpen && (
                                    <div className="border-rule">
                                        <div className="rule-content">
                                            {rule.isProtocolView && <div className="nested-indicator"/>}
                                            <div
                                                className="content-wrapper"
                                                dangerouslySetInnerHTML={{__html: rule.content}}
                                            />
                                        </div>

                                        {rule.statements && rule.statements.length > 0 && (
                                            <div className="nested-statements">
                                                {rule.statements.map((statement) => (
                                                    <div
                                                        className="statement-box"
                                                        key={statement.id}
                                                        style={{background: statementBg(statement.conditionResult)}}
                                                    >
                                                        <div className="nested-indicator"/>
                                                        <div
                                                            className="statement-content"
                                                            dangerouslySetInnerHTML={{__html: statement.content}}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="evidence-rule-container">
                                {rule.content && (
                                    <div className="evidence-title-box">
                                        {/* Plain text on purpose - the nested card's title is a
                                            sentence the service writes without markup. */}
                                        <div className="evidence-title-content">{rule.content}</div>
                                    </div>
                                )}

                                <div className="evidence-statements">
                                    {(rule.statements || []).map((statement, statementIndex) => {
                                        const statementKey = `${ruleIndex}:${statement.id}`;
                                        const isStatementOpen = Boolean(expanded[statementKey]);
                                        const isLast = statementIndex === (rule.statements || []).length - 1;

                                        return (
                                            <div className="evidence-statement-wrapper" key={statement.id}>
                                                <button
                                                    type="button"
                                                    className="toggle-btn small"
                                                    onClick={() => toggle(statementKey)}
                                                >
                                                    {isStatementOpen ? '−' : '+'}
                                                </button>
                                                <div className="statement-number">{statement.id}</div>

                                                {isStatementOpen && (
                                                    // The outer frame takes the RULE's colour, the box
                                                    // inside it the STATEMENT's.
                                                    <div
                                                        className="border-rule"
                                                        style={{border: borderColor(rule.conditionResult)}}
                                                    >
                                                        <div
                                                            className="statement-box"
                                                            style={{
                                                                border: borderColor(statement.conditionResult),
                                                                background: statementBg(statement.conditionResult),
                                                            }}
                                                        >
                                                            <div className="nested-indicator"/>
                                                            <div
                                                                className="statement-content"
                                                                dangerouslySetInnerHTML={{__html: statement.content}}
                                                            />
                                                        </div>
                                                    </div>
                                                )}

                                                {!isLast && rule.operator && (
                                                    <div className="operator-separator">{rule.operator}</div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

MedicalRules.propTypes = {
    jsonData: PropTypes.object,
    viewType: PropTypes.oneOf(['simple', 'protocol']),
    type: PropTypes.oneOf([
        'Protocols', 'Diagnosis Rule', 'Procedure Rule', 'Clinical Terms Rule', 'Document Rule',
    ]),
};

MedicalRules.defaultProps = {
    jsonData: null,
    // Carried over from the Angular component's input default. The filter above keys off
    // `type`; the service is always asked for the simple view from this screen.
    viewType: 'protocol',
    type: 'Protocols',
};

export default MedicalRules;
